/*
 * QUEST SOFTWARE PROPRIETARY INFORMATION
 *
 * This software is confidential.  Quest Software Inc., or one of its
 * subsidiaries, has supplied this software to you under terms of a
 * license agreement, nondisclosure agreement or both.
 *
 * You may not copy, disclose, or use this software except in accordance with
 * those terms.
 *
 *
 * Copyright 2026 Quest Software Inc.
 * ALL RIGHTS RESERVED.
 *
 * QUEST SOFTWARE INC. MAKES NO REPRESENTATIONS OR
 * WARRANTIES ABOUT THE SUITABILITY OF THE SOFTWARE,
 * EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
 * TO THE IMPLIED WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE, OR
 * NON-INFRINGEMENT.  QUEST SOFTWARE SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE
 * AS A RESULT OF USING, MODIFYING OR DISTRIBUTING
 * THIS SOFTWARE OR ITS DERIVATIVES.
 */

import { Injectable, OnDestroy } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { ALIAS_QUERY_MAP, BREAKPOINT_MAP } from '../models/layout.model';

@Injectable({
  providedIn: 'root'
})
export class MatchMediaService implements OnDestroy {
  activeMediaQuery: string;
  onMediaChange = new BehaviorSubject<string>('');

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  /**
   * Constructor
   *
   * @param {BreakpointObserver} _breakpointObserver
   */
  constructor(private _breakpointObserver: BreakpointObserver) {
    // Set the defaults
    this.activeMediaQuery = '';

    // Initialize
    this._init();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /**
   * Check if a media query alias is active.
   *
   * @param alias The media query alias to check.
   * @returns {boolean}
   */
  isActive(alias: string): boolean {
    const queries = ALIAS_QUERY_MAP.get(alias) || [];
    return this._breakpointObserver.isMatched(queries);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Initialize
   *
   * @private
   */
  private _init(): void {
    this._breakpointObserver.observe(Array.from(BREAKPOINT_MAP.keys()))
      .pipe(
        takeUntil(this._unsubscribeAll),
        debounceTime(500),
        map(result => {
          for (const query of Array.from(BREAKPOINT_MAP.keys())) {
            if (result.breakpoints[query]) {
              return BREAKPOINT_MAP.get(query);
            }
          }
          return undefined;
        }),
        distinctUntilChanged()
      )
      .subscribe((mqAlias) => {
        if (mqAlias && this.activeMediaQuery !== mqAlias) {
          this.activeMediaQuery = mqAlias;
          this.onMediaChange.next(mqAlias);
        }
      });
  }
}
